import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogTitle,
  Button,
  DialogContent,
  DialogContentText,
  RadioGroup,
  FormControlLabel,
  Radio,
  DialogActions,
  useTheme,
  styled,
} from "@mui/material";
import axios from "axios";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { Common } from "../../../../common/src";
import { config } from "../../../config/config";
import {
  EDisplayType,
  ProjectDetailContext,
  UseSnackbarContext,
} from "../Interfaces/ProjectDetailInterfaces";
import { padding, style, typography } from "@mui/system";
import FileManagerIconDark from "../../../../images/icons/neutral_edit/icon_filemanager.svg";
import FileManagerIconLight from "../../../../images/icons/neutral_edit/icon_filemanager_black.png";
import Icon from "../../Icon";

interface ExportProps {
  open: boolean;
  close: () => void;
}

const ExportData = (exportProps: ExportProps) => {
  const { projectDetail, displayType, currentPointCloud, viewer } =
    useContext(ProjectDetailContext);
  const { openSuccess, openError } = useContext(UseSnackbarContext);

  const [whileLoading, setWhileLoading] = useState(false);
  const [checkedLiDARPoints, setCheckedLiDARPoints] = useState(true);
  const [checkedLasSfm, setCheckedLasSfm] = useState(false);
  const [checkedLasHelmert, setCheckedLasHelmert] = useState(false);
  const [checkedReferencePoints, setCheckedReferencePoints] = useState(false);
  const [checkedVerificationPoints, setCheckedVerificationPoints] =
    useState(false);
  const [checkedImages, setCheckedImages] = useState(false);
  const [checkedQualityReport, setQualityReport] = useState(false);
  const [dlFileType, setDlFileType] = useState<string>("LAS"); //

  const [disabledSfM, setDisabledSfM] = useState<boolean>(true);
  const [disabledHelmert, setDisabledHelmert] = useState<boolean>(true);
  const [disabledReferencePoints, setDisabledReferencePoints] =
    useState<boolean>(true);
  const [disabledVerificationPoints, setDisabledVerificationPoints] =
    useState<boolean>(true);

  const [abortController, setAbortController] = useState<AbortController>(
    new AbortController()
  );
  const [disabledLINK, setDisabledLINK] = useState<boolean>(false);
  const [filemanagerURL, setFileManagerURL] = useState<string>("");

  useEffect(() => {
    axios
      .get(
        `${config.serverHost}/api/v1/project/${projectDetail?.id}/file-storage/node`,
        {
          withCredentials: true,
        }
      )
      .then((res) => {
        setFileManagerURL(
          `${process.env.REACT_APP_GROUPWARE_URL}/share?bucket=${res.data?.bucketId}&node=${res.data?.nodeId}`
        );
        setDisabledLINK(false);
      })
      .catch(() => {
        setDisabledLINK(true);
      });
  }, []);

  const initExportDialogData = () => {
    setCheckedLiDARPoints(true);
    setCheckedLasSfm(false);
    setCheckedLasHelmert(false);
    setCheckedReferencePoints(false);
    setCheckedVerificationPoints(false);
    setCheckedImages(false);
    setQualityReport(false);
    setDlFileType("LAS");
  };

  useEffect(() => {
    initExportDialogData();
    setDisabledSfM(
      projectDetail?.status.verification_sfm !==
        Common.PROJECT_STATUS_ENUM.COMPLETED &&
        projectDetail?.status.sfm_processing !==
          Common.PROJECT_STATUS_ENUM.COMPLETED &&
        projectDetail?.status.helmert_transformation !==
          Common.PROJECT_STATUS_ENUM.COMPLETED &&
        projectDetail?.status.verification_helmert !==
          Common.PROJECT_STATUS_ENUM.COMPLETED
    );
    setDisabledHelmert(
      projectDetail?.status.helmert_transformation !==
        Common.PROJECT_STATUS_ENUM.COMPLETED &&
        projectDetail?.status.verification_helmert !==
          Common.PROJECT_STATUS_ENUM.COMPLETED
    );
    setDisabledReferencePoints(
      projectDetail?.status.verification_sfm !==
        Common.PROJECT_STATUS_ENUM.COMPLETED &&
        projectDetail?.status.sfm_processing !==
          Common.PROJECT_STATUS_ENUM.COMPLETED &&
        projectDetail?.status.helmert_transformation !==
          Common.PROJECT_STATUS_ENUM.COMPLETED &&
        projectDetail?.status.verification_helmert !==
          Common.PROJECT_STATUS_ENUM.COMPLETED
    );
    setDisabledVerificationPoints(
      !(
        projectDetail?.status.verification_sfm ===
          Common.PROJECT_STATUS_ENUM.COMPLETED ||
        projectDetail?.status.verification_helmert ===
          Common.PROJECT_STATUS_ENUM.COMPLETED
      )
    );
    setAbortController(new AbortController());
  }, [exportProps.open]);

  const downloadProjectData = async (input: Common.ProjectDownloadAllInput) => {
    return new Promise<void>((resolve, reject) => {
      const params = {
        ...input,
        snapshot: input.verificationPoints
          ? viewer.getCanvas().toDataURL("image/jpeg")
          : "",
      };
      axios
        .post(
          `${config.serverHost}/api/v1/project/${projectDetail?.id}/download`,
          params,
          {
            withCredentials: true,
            responseType: "blob",
            signal: abortController.signal,
          }
        )
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const a = document.createElement("a");
          a.href = url;

          const contentDisposition = res.headers["content-disposition"];
          const regex = /filename=(.+)/;
          const match = contentDisposition?.match(regex);
          if (match) {
            a.download = decodeURIComponent(match[1].trim());
          } else {
            a.download = "noname";
          }

          a.click();
          // ダウンロード後は不要なのでaタグを除去
          a.remove();
          resolve();
        })
        .catch((err) => {
          if (err.message !== "canceled") {
            openError(t("DOWNLOAD_ERROR"));
            reject();
          }
        });
    });
  };

  const handleDownloadCancel = async () => {
    if (whileLoading) {
      openSuccess(t("CANCELLED"));
      abortController.abort();
    }
    exportProps.close();
    setWhileLoading(false);
  };

  const handleDownloadOK = async () => {
    setWhileLoading(true);
    let input: Common.ProjectDownloadAllInput = {
      LiDAR: checkedLiDARPoints,
      sfm: checkedLasSfm,
      helmert: checkedLasHelmert,
      referencePoints: checkedReferencePoints,
      verificationPoints: checkedVerificationPoints,
      images: checkedImages,
      qualityReport: checkedQualityReport,
    };
    (checkedLasSfm || checkedLasHelmert) &&
      (input = { ...input, fileType: dlFileType });

    if (displayType === EDisplayType.Web || input.verificationPoints) {
      await downloadProjectData(input)
        .then(() => {
          setWhileLoading(false);
          openSuccess(t("DOWNLOAD_COMPLETED"));
        })
        .catch(() => {
          setWhileLoading(false);
        });
    } else {
      if (!input.fileType) input = { ...input, fileType: dlFileType };
      (window as any).webkit.messageHandlers.callback.postMessage({
        type: "download",
        props: input,
      });
    }
  };

  const successDownload = () => {
    setWhileLoading(false);
    openSuccess(t("DOWNLOAD_COMPLETED"));
  };
  (window as any)["successDownload"] = successDownload;

  const errorDownload = () => {
    setWhileLoading(false);
    openError(t("DOWNLOAD_ERROR"));
  };
  (window as any)["errorDownload"] = errorDownload;

  const handleOnChangeLiDARPoints = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCheckedLiDARPoints(event.target.checked);
    setCheckedLasSfm(!event.target.checked);
    setCheckedLasHelmert(!event.target.checked);
    setCheckedReferencePoints(!event.target.checked);
    setCheckedVerificationPoints(!event.target.checked);
    setCheckedImages(!event.target.checked);
    setQualityReport(!event.target.checked);
  };

  const handleOnChangeLasSfm = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedLiDARPoints(!event.target.checked);
    setCheckedLasSfm(event.target.checked);
    setCheckedLasHelmert(!event.target.checked);
    setCheckedReferencePoints(!event.target.checked);
    setCheckedVerificationPoints(!event.target.checked);
    setCheckedImages(!event.target.checked);
    setQualityReport(!event.target.checked);
  };

  const handleOnChangeLasHelmert = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCheckedLiDARPoints(!event.target.checked);
    setCheckedLasSfm(!event.target.checked);
    setCheckedLasHelmert(event.target.checked);
    setCheckedReferencePoints(!event.target.checked);
    setCheckedVerificationPoints(!event.target.checked);
    setCheckedImages(!event.target.checked);
    setQualityReport(!event.target.checked);
  };

  const handleOnChangeReferencePoints = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCheckedLiDARPoints(!event.target.checked);
    setCheckedLasSfm(!event.target.checked);
    setCheckedLasHelmert(!event.target.checked);
    setCheckedReferencePoints(event.target.checked);
    setCheckedVerificationPoints(!event.target.checked);
    setCheckedImages(!event.target.checked);
    setQualityReport(!event.target.checked);
  };

  const handleOnChangeVerificationPoints = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCheckedLiDARPoints(!event.target.checked);
    setCheckedLasSfm(!event.target.checked);
    setCheckedLasHelmert(!event.target.checked);
    setCheckedReferencePoints(!event.target.checked);
    setCheckedVerificationPoints(event.target.checked);
    setCheckedImages(!event.target.checked);
    setQualityReport(!event.target.checked);
  };

  const handleOnChangeImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedLiDARPoints(!event.target.checked);
    setCheckedLasSfm(!event.target.checked);
    setCheckedLasHelmert(!event.target.checked);
    setCheckedReferencePoints(!event.target.checked);
    setCheckedVerificationPoints(!event.target.checked);
    setCheckedImages(event.target.checked);
    setQualityReport(!event.target.checked);
  };

  const handleOnChangeQualityReport = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCheckedLiDARPoints(!event.target.checked);
    setCheckedLasSfm(!event.target.checked);
    setCheckedLasHelmert(!event.target.checked);
    setCheckedReferencePoints(!event.target.checked);
    setCheckedVerificationPoints(!event.target.checked);
    setCheckedImages(!event.target.checked);
    setQualityReport(event.target.checked);
  };

  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  const customModalColor = {
    color: theme.palette.base.modal,
  };

  const customDialogBackgroundColor = {
    color: theme.palette.base.dialogBackground,
  };

  const customLoadingButtonColor = {
    background: theme.palette.primary.main,
    boxShadow: "none",
  };

  const CustomCanselButton = styled(Button)({
    background: theme.palette.base.modal,
    color: theme.palette.base.contrast,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  const CustomIcon = () => {
    return theme.palette.mode === "dark" ? (
      <img src={FileManagerIconDark} width={30} height={30} alt="" />
    ) : (
      <img src={FileManagerIconLight} width={30} height={30} alt="" />
    );
  };

  return (
    <>
      <Dialog
        open={exportProps.open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          className: "overflow12",
          style: { background: customModalColor.color },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: customDialogBackgroundColor.color,
            },
          },
        }}
      >
        <DialogTitle id="alert-dialog-title" className="overflow13">
          {t("EXPORT_DATA")}
          <Button
            style={customColor}
            className="x-btn overflow14"
            onClick={handleDownloadCancel}
          >
            ×
          </Button>
          <div className="overflow15" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText style={{ textAlign: "left", ...customColor }}>
            {t("SELECT_FILE_EXPORT")}
          </DialogContentText>
          <RadioGroup>
            <div style={{ display: "flex" }}>
              <FormControlLabel
                control={
                  <Radio
                    checked={checkedLiDARPoints}
                    onChange={handleOnChangeLiDARPoints}
                  />
                }
                label={t("LiDAR_POINT_CLOUD")}
                componentsProps={{
                  typography: {
                    sx: {
                      lineHeight: "1.0",
                      width: "220px",
                      textAlign: "left",
                    },
                  },
                }}
                style={{ margin: 0 }}
              />
              {/* <FormControlLabel
                control={
                  <Radio
                    checked={checkedVerificationPoints}
                    onChange={handleOnChangeVerificationPoints}
                  />
                }
                disabled={disabledVerificationPoints}
                label={t("ACCURACY_VALIDATION")}
                componentsProps={{
                  typography: { sx: { lineHeight: "1.0", textAlign: "left" } },
                }}
                style={{ margin: 0 }}
              /> */}
              <FormControlLabel
                control={
                  <Radio
                    checked={checkedReferencePoints}
                    onChange={handleOnChangeReferencePoints}
                  />
                }
                disabled={disabledReferencePoints}
                label={t("ORIENTATION_INFOR")}
                componentsProps={{
                  typography: { sx: { lineHeight: "1.0", textAlign: "left" } },
                }}
                style={{ margin: 0 }}
              />
            </div>
            <div style={{ display: "flex" }}>
              <FormControlLabel
                control={
                  <Radio
                    checked={checkedLasSfm}
                    onChange={handleOnChangeLasSfm}
                  />
                }
                disabled={disabledSfM}
                label={t("ORIGINAL_SFM_POINT")}
                componentsProps={{
                  typography: {
                    sx: {
                      lineHeight: "1.0",
                      width: "220px",
                      textAlign: "left",
                    },
                  },
                }}
                style={{ margin: 0 }}
              />
              <FormControlLabel
                control={
                  <Radio
                    checked={checkedQualityReport}
                    onChange={handleOnChangeQualityReport}
                  />
                }
                label={t("QUALITY_REPORT")}
                componentsProps={{
                  typography: { sx: { lineHeight: "1.0", textAlign: "left" } },
                }}
                style={{ margin: 0 }}
              />
            </div>
            <div style={{ display: "flex" }}>
              <FormControlLabel
                control={
                  <Radio
                    checked={checkedLasHelmert}
                    onChange={handleOnChangeLasHelmert}
                  />
                }
                disabled={disabledHelmert}
                label={t("TRANSFOR_SFM_POINT")}
                componentsProps={{
                  typography: {
                    sx: {
                      lineHeight: "1.0",
                      width: "220px",
                      textAlign: "left",
                    },
                  },
                }}
                style={{ margin: 0 }}
              />
              <FormControlLabel
                control={
                  <Radio
                    checked={checkedImages}
                    onChange={handleOnChangeImages}
                  />
                }
                label={t("PHOTOS")}
                componentsProps={{
                  typography: {
                    sx: {
                      lineHeight: "1.0",
                      width: "220px",
                      textAlign: "left",
                    },
                  },
                }}
                style={{ margin: 0 }}
              />
            </div>
          </RadioGroup>
          <DialogContentText
            style={{ marginTop: "10px", textAlign: "left", ...customColor }}
          >
            {t("SELECT_POINT_FORMAT")}
          </DialogContentText>
          <div style={{ display: "flex", alignItems: "center" }}>
            <RadioGroup>
              <div style={{ display: "flex", marginLeft: "0px" }}>
                <FormControlLabel
                  disabled={!(checkedLasSfm || checkedLasHelmert)}
                  control={
                    <Radio
                      checked={dlFileType === "LAS"}
                      onChange={() => setDlFileType("LAS")}
                    />
                  }
                  label={".las"}
                  style={{ color: customColor.color, margin: 0 }}
                />
                <FormControlLabel
                  disabled={!(checkedLasSfm || checkedLasHelmert)}
                  control={
                    <Radio
                      checked={dlFileType === "TXT"}
                      onChange={() => setDlFileType("TXT")}
                    />
                  }
                  label={".txt"}
                  style={{ color: customColor.color, margin: 0 }}
                />
              </div>
            </RadioGroup>
            {displayType === EDisplayType.Web ? (
              <>
                <DialogContentText
                  style={{ color: customColor.color, marginLeft: "140px" }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <CustomIcon />
                    {disabledLINK ? (
                      <a
                        style={{
                          color: "#666666",
                          whiteSpace: "normal",
                          marginLeft: "2px",
                          textAlign: "left",
                        }}
                      >
                        {t("OPEN_SC_GROUPWARE")}
                      </a>
                    ) : (
                      <a
                        target="_blank"
                        style={{
                          color: customColor.color,
                          whiteSpace: "normal",
                          marginLeft: "2px",
                          textAlign: "left",
                        }}
                        href={filemanagerURL}
                      >
                        {t("OPEN_SC_GROUPWARE")}
                      </a>
                    )}
                  </div>
                </DialogContentText>
              </>
            ) : (
              <></>
            )}
          </div>
        </DialogContent>
        <DialogActions className="overflow26">
          <CustomCanselButton
            size="small"
            variant="contained"
            onClick={handleDownloadCancel}
            className="overflow27 overflow28"
          >
            {t("CANCEL")}
          </CustomCanselButton>
          <LoadingButton
            style={whileLoading ? null : customLoadingButtonColor}
            size="small"
            variant="contained"
            loading={whileLoading}
            onClick={handleDownloadOK}
            color="primary"
            className="loading-white overflow27"
          >
            {t("EXPORT")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExportData;
